import { Box, Button, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useLoadMoreIfEndOfScreen } from "@outschool/ui-utils";
import React from "react";

import {
  UpcomingClassesQueryOptions,
  useUpcomingClassesQuery
} from "../../../../queries/UpcomingClassesModalQuery";
import ErrorMessage from "../../../ErrorMessage";
import { UpcomingClasses } from "./UpcomingClasses";

type UpcomingClassesInfiniteScrollProps = {
  leaderUid: string;
};

export const UpcomingClassesInfiniteScroll: React.FunctionComponent<
  UpcomingClassesInfiniteScrollProps
> = ({ leaderUid }) => {
  const options: UpcomingClassesQueryOptions = {
    variables: {
      uid: leaderUid,
      includeSchedule: true,
      flexSectionsOffset: "0",
      meetingsOffset: "0"
    }
  };

  const {
    data,
    error,
    loading,
    fetchMoreClasses,
    canFetchMore,
    isFetchingMore
  } = useUpcomingClassesQuery(options);

  const [endOfScreenRef, setEndOfScreenRef] = React.useState<HTMLDivElement>();
  useLoadMoreIfEndOfScreen({
    endOfScreenRef,
    fetchMore: fetchMoreClasses,
    isInfiniteScrollEnabled: true
  });

  /** handle refetch and refetch errors */
  const [refetchError, setRefetchError] = React.useState<string | null>(null);
  const handleFetchMoreClasses = React.useCallback(async () => {
    try {
      await fetchMoreClasses();
    } catch (e) {
      setRefetchError(e);
    }
  }, [fetchMoreClasses]);
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherProfile\\UpcomingClassesInfiniteScroll"
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {/* @ts-ignore TS(2531): Object is possibly 'null'. */}
      <UpcomingClasses leaderData={data.publicProfile.leader} />

      {canFetchMore && (
        <Box
          ref={setEndOfScreenRef}
          sx={{
            height: isFetchingMore ? "0px" : "10px"
          }}
        />
      )}

      {/* if there's an error, give the user the option to manually fetch more classes */}
      {(error || refetchError) && (
        <>
          <ErrorMessage
            value={t("Oops, we encountered an error when loading classes.")}
          />
          <Box
            flex
            sx={{
              justifyContent: "center"
            }}
          >
            <Button onClick={handleFetchMoreClasses}>
              {t("Try loading more classes again")}
            </Button>
          </Box>
        </>
      )}

      {isFetchingMore && <Loading delay={0} />}

      {!canFetchMore && (
        <Box>
          <Typography
            variant="inherit"
            sx={{
              textAlign: "center",
              display: "block",
              paddingY: "20px"
            }}
          >
            {t("No more classes to show")}
          </Typography>
        </Box>
      )}
    </>
  );
};
